/**
 * InvoiceDetail.tsx
 * Slide-over panel showing full invoice details — line items, timeline, actions.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface InvoiceLineItem {
  description: string;
  amount: number; // cents
  quantity: number;
}

interface StripeInvoice {
  id: string;
  number?: string;
  status: string;
  customerName?: string;
  customerEmail?: string;
  amountDue: number; // cents
  dueDate?: string;
  created: string;
  finalizedAt?: string;
  sentAt?: string;
  paidAt?: string;
  voidedAt?: string;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
  lineItems: InvoiceLineItem[];
  description?: string;
  footer?: string;
}

interface Props {
  invoiceId: string;
  onClose: () => void;
  onAction: () => void; // refresh parent list
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string }) {
  const classes: Record<string, string> = {
    draft: "bg-slate-100 text-slate-600",
    open: "bg-blue-100 text-blue-700",
    paid: "bg-emerald-100 text-emerald-700",
    void: "bg-red-100 text-red-600",
    uncollectible: "bg-orange-100 text-orange-700",
  };
  const cls = classes[status] ?? "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export default function InvoiceDetail({ invoiceId, onClose, onAction }: Props) {
  const api = useApi();
  const toastAlert = useToastAlert();

  const [invoice, setInvoice] = useState<StripeInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.admin.get(`api/StripeInvoice/${invoiceId}`);
      if (resp.ok) {
        const data = await resp.json();
        setInvoice(data);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [api.admin, invoiceId]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  async function handleSend() {
    if (!invoice) return;
    setActing("send");
    try {
      const resp = await api.admin.post(`api/StripeInvoice/${invoice.id}/send`, {});
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Invoice sent to customer.");
        onAction();
        await fetchInvoice();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to send invoice.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to send invoice.");
    } finally {
      setActing(null);
    }
  }

  async function handleVoid() {
    if (!invoice) return;
    if (!confirm("Void this invoice? This cannot be undone.")) return;
    setActing("void");
    try {
      const resp = await api.admin.post(`api/StripeInvoice/${invoice.id}/void`, {});
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Invoice voided.");
        onAction();
        await fetchInvoice();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to void invoice.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to void invoice.");
    } finally {
      setActing(null);
    }
  }

  function handleCopyUrl() {
    if (invoice?.hostedInvoiceUrl) {
      navigator.clipboard.writeText(invoice.hostedInvoiceUrl).then(() => {
        toastAlert.displayToast(Severity.Success, "Invoice URL copied to clipboard.");
      }).catch(() => {
        toastAlert.displayToast(Severity.Error, "Could not copy URL.");
      });
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/30 flex items-end justify-end" onClick={onClose}>
      <div
        className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-800">
              {invoice?.number ? `Invoice ${invoice.number}` : "Invoice"}
            </h2>
            {invoice && <StatusBadge status={invoice.status} />}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center flex-1">
            <i className="fa-solid fa-spinner fa-spin text-slate-400 text-xl" />
          </div>
        ) : invoice ? (
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Customer & Amount */}
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-800">
                  {invoice.customerName ?? "Unknown Customer"}
                </p>
                <p className="text-xs text-slate-500">{invoice.customerEmail}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-800">
                  {formatCurrency(invoice.amountDue)}
                </p>
                {invoice.dueDate && (
                  <p className="text-xs text-slate-500">Due {formatDate(invoice.dueDate)}</p>
                )}
              </div>
            </div>

            {invoice.description && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase mb-1">Description</p>
                <p className="text-sm text-slate-700">{invoice.description}</p>
              </div>
            )}

            {/* Line Items */}
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase mb-2">Line Items</p>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-[1fr_60px_90px] bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500 uppercase border-b border-slate-200">
                  <span>Description</span>
                  <span className="text-center">Qty</span>
                  <span className="text-right">Amount</span>
                </div>
                {(invoice.lineItems ?? []).map((li, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[1fr_60px_90px] px-3 py-2 border-b border-slate-100 last:border-0 text-sm"
                  >
                    <span className="text-slate-700">{li.description}</span>
                    <span className="text-center text-slate-500">{li.quantity}</span>
                    <span className="text-right tabular-nums text-slate-800">
                      {formatCurrency(li.amount)}
                    </span>
                  </div>
                ))}
                <div className="grid grid-cols-[1fr_60px_90px] px-3 py-2 bg-slate-50 border-t border-slate-200 text-sm font-semibold text-slate-800">
                  <span>Total</span>
                  <span />
                  <span className="text-right tabular-nums">{formatCurrency(invoice.amountDue)}</span>
                </div>
              </div>
            </div>

            {invoice.footer && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase mb-1">Footer Notes</p>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{invoice.footer}</p>
              </div>
            )}

            {/* Timeline */}
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase mb-3">Timeline</p>
              <div className="space-y-2">
                {[
                  { label: "Created", date: invoice.created },
                  { label: "Finalized", date: invoice.finalizedAt },
                  { label: "Sent", date: invoice.sentAt },
                  { label: "Paid", date: invoice.paidAt },
                  { label: "Voided", date: invoice.voidedAt },
                ]
                  .filter((e) => e.date)
                  .map((e) => (
                    <div key={e.label} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-cornflower-blue-400 shrink-0" />
                      <span className="text-xs text-slate-500 w-20">{e.label}</span>
                      <span className="text-xs text-slate-700">{formatDate(e.date)}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Customer Payment Link panel */}
            {invoice.hostedInvoiceUrl && (invoice.status === "open" || invoice.status === "draft") && (
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-sm font-medium text-slate-700 mb-2">Customer Payment Link</div>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={invoice.hostedInvoiceUrl}
                    className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white font-mono text-slate-600 truncate"
                  />
                  <button
                    onClick={handleCopyUrl}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-600 transition-colors whitespace-nowrap"
                  >
                    Copy Link
                  </button>
                  <a
                    href={invoice.hostedInvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-cornflower-blue-500 hover:bg-cornflower-blue-600 rounded-lg text-sm text-white transition-colors whitespace-nowrap"
                  >
                    Open
                  </a>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              {invoice.status === "open" && (
                <button
                  onClick={handleSend}
                  disabled={acting === "send"}
                  className="px-3 py-1.5 text-sm rounded-md bg-cornflower-blue-600 text-white hover:bg-cornflower-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {acting === "send" && <i className="fa-solid fa-spinner fa-spin" />}
                  <i className="fa-solid fa-paper-plane" /> Send
                </button>
              )}

              {(invoice.status === "open" || invoice.status === "draft") && (
                <button
                  onClick={handleVoid}
                  disabled={acting === "void"}
                  className="px-3 py-1.5 text-sm rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 flex items-center gap-2"
                >
                  {acting === "void" && <i className="fa-solid fa-spinner fa-spin" />}
                  <i className="fa-solid fa-ban" /> Void
                </button>
              )}

              {invoice.invoicePdf && (
                <a
                  href={invoice.invoicePdf}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                >
                  <i className="fa-solid fa-file-pdf" /> Download PDF
                </a>
              )}

              {invoice.hostedInvoiceUrl && (
                <>
                  <a
                    href={invoice.hostedInvoiceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square" /> View Online
                  </a>
                  <button
                    onClick={handleCopyUrl}
                    className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <i className="fa-solid fa-copy" /> Copy URL
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
            Invoice not found.
          </div>
        )}
      </div>
    </div>
  );
}
